import { inject } from '@angular/core';
import { CanActivateChildFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { ACCESS_SUSPENDED_PAGE } from '@pages/access-suspended/access-suspended.constants';
import { BILLING_PAGE } from '@pages/billing/billing.constants';
import { UserState } from '@shared/states/user.state';

const isAlreadyOn = (url: string, page: string): boolean => {
  const path = url.split(/[?#]/)[0];

  return path === page || path.startsWith(`${page}/`);
};

/**
 * Blocks every in-app page once the payment grace period has fully elapsed (days left < 0) and
 * sends the user to a single allowed destination — every role, including clients:
 *
 * - holders of BILLING_VIEW/BILLING_MODIFY go to the billing page, so they can settle the invoice
 *   and unblock the tenant themselves (this is also what makes them land there right after login,
 *   since the post-login URL bounces off this guard);
 * - everyone else goes to the suspended screen, whose only action is logging out.
 *
 * Both destinations live inside the layout, so the navbar still renders; NavbarComponent dims it
 * rather than letting the links bounce off this guard.
 */
export const PaymentSuspendedCanActivateChildGuard: CanActivateChildFn = (
  _,
  state: RouterStateSnapshot,
): boolean | UrlTree => {
  const userState = inject(UserState);

  if (!userState.isAccessSuspended) {
    return true;
  }

  const allowedPage = userState.canResolvePaymentOverdue ? BILLING_PAGE : ACCESS_SUSPENDED_PAGE;

  // Without this the guard would keep redirecting the destination onto itself.
  return isAlreadyOn(state.url, allowedPage) ? true : inject(Router).createUrlTree([allowedPage]);
};
